// ChatPanel.js — чат по потокам/группам (экран внутри приложения).
// Новый модуль фронтенда (не трогает существующие экраны).
// Общается с новой облачной функцией chat-sync через api.js.
//
// Агент чата:
//   - ЭКРАН «список потоков» (для user/mentor/админа) — то, что видно по getMyStreams.
//   - ЭКРАН «открытая комната» — лента, отправка (Enter / Shift+Enter), удаление.
//   - markRead вызывается при открытии комнаты (вариант B: персональный lastSeen).
//   - Поллинг каждые 5 секунд (08:00–23:00 МСК) — список + открытая комната.
//
// Время сообщений — хранится на сервере в UTC ISO, показываем всегда по МСК (UTC+3).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { chatGetMyStreams, chatGetMessages, chatSendMessage, chatDeleteMessage, chatEditMessage, chatMarkRead, reportRunNow } from './api';
import { Icons } from './icons';

const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;
const POLL_MS = 5000;

// --- утилиты ---------------------------------------------------------------

// ISO(UTC) → «чч:мм» по МСК, для прошлых дней ещё «дд.мм чч:мм»
const formatMskTime = (iso) => {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime() + MSK_OFFSET_MS;
    const d = new Date(t);
    const nowT = Date.now() + MSK_OFFSET_MS;
    const now = new Date(nowT);
    const hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    const sameDay = d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth() && d.getUTCDate() === now.getUTCDate();
    if (sameDay) return hm;
    return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')} ${hm}`;
  } catch (e) {
    return '';
  }
};

// true когда сейчас в окне 08:00–22:59 МСК (поллинг активен только днём)
const isMskWindowOpen = () => {
  const now = new Date(Date.now() + MSK_OFFSET_MS);
  const h = now.getUTCHours();
  return h >= 8 && h < 23;
};

// Сравнение «массивов сообщений» по id/time/text (для гашения лишних ре-рендеров)
const deepEqualMessages = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (!x || !y || x.id !== y.id || x.time !== y.time || x.text !== y.text) return false;
  }
  return true;
};

// Кто «я» в справке потока (для отображения подписи «вы»).
const isMine = (login, msg) => msg && msg.user === login;

export const isChatWindowOpen = isMskWindowOpen;

// --- компонент -------------------------------------------------------------

function ChatPanel({ getAuth, onClose }) {
  const { colors } = useTheme();

  // Список потоков (то, что видит пользователь)
  const [streams, setStreams] = useState(null);          // null — ещё грузится
  const [streamsError, setStreamsError] = useState(null);
  const [role, setRole] = useState('user');
  // Отчёт по нарушениям: кнопка в шапке комнаты (только для наставника/админа).
  const [reportBusy, setReportBusy] = useState(false);

  // Открытая комната
  const [activeStream, setActiveStream] = useState(null); // {id, name}
  const [messages, setMessages] = useState([]);
  const [chatError, setChatError] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  // Долгое нажатие → меню сообщения
  const [menuMsg, setMenuMsg] = useState(null);
  // Режим редактирования своего сообщения (ждёт действия edit на сервере)
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState('');
  const [toast, setToast] = useState('');
  // ref таймера долгого нажатия
  const holdTimerRef = useRef(null);

  const streamIdRef = useRef(null);
  useEffect(() => { streamIdRef.current = activeStream ? activeStream.id : null; }, [activeStream]);

  const auth = getAuth();
  const myLogin = auth ? auth.login : null;

  const listEndRef = useRef(null);

  // --- загрузка списка моих потоков ----------------------------------------
  const loadStreams = useCallback(async (silent = false) => {
    const a = getAuth();
    if (!a || !a.login || !a.token) return;
    try {
      const d = await chatGetMyStreams(a.login, a.token);
      if (d.success) {
        setStreams(d.streams || []);
        setRole(d.role || a.role || 'user');
        if (!silent) setStreamsError(null);
      } else if (!silent) {
        setStreamsError(d.error || 'Не удалось загрузить потоки');
        setStreams(prev => prev || []);
      }
    } catch (e) {
      if (!silent) {
        setStreamsError('Не удалось связаться с сервером.');
        setStreams(prev => prev || []);
      }
    }
  }, [getAuth]);

  // Первичная загрузка
  useEffect(() => { loadStreams(); }, [loadStreams]);

  // --- открыть комнату -------------------------------------------------------
  const openStream = async (stream) => {
    const a = getAuth();
    if (!a) return;
    setActiveStream({ id: stream.id, name: stream.name });
    setMessages([]);
    setChatError(null);
    setLoadingMsgs(true);
    try {
      const g = await chatGetMessages(a.login, a.token, stream.id);
      if (g.success) {
        setMessages(g.messages || []);
        if (g.stream && g.stream.name) setActiveStream({ id: stream.id, name: g.stream.name });
      } else {
        setChatError(g.error || 'Не удалось загрузить сообщения');
      }
      // markRead — вариант B: при открытии комнаты
      try { await chatMarkRead(a.login, a.token, stream.id); } catch (e2) {}
      // обновим статус непрочитанного в списке
      setStreams(prev => prev ? prev.map(s => s.id === stream.id ? { ...s, unread: false } : s) : prev);
    } catch (e) {
      setChatError('Не удалось связаться с сервером.');
    } finally {
      setLoadingMsgs(false);
    }
  };

  const closeStream = () => {
    // Перед уходом из комнаты отмечаем прочитанное: сообщения могли прийти,
    // пока комната была открыта (тогда markRead при открытии уже устарел).
    const a = getAuth();
    if (a && activeStream) {
      const sid = activeStream.id;
      try { chatMarkRead(a.login, a.token, sid); } catch (e) { /* тихо */ }
      setStreams(prev => prev ? prev.map(s => s.id === sid ? { ...s, unread: false } : s) : prev);
    }
    setActiveStream(null);
    setMessages([]);
    setChatError(null);
    setMenuMsg(null);
    setEditingMsg(null);
    setEditText('');
  };

  // Короткие уведомления (копия/ошибка копирования).
  // 3000 мс, как и во всех остальных уведомлениях (решение владельца 2026-09-12:
  // было 2200 — уведомление мелькало и не успевало прочитаться).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Открыть меню сообщения (долгое нажатие / правый клик)
  const openMenu = (msg) => {
    setMenuMsg(msg);
  };

  const copyMessage = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.text || '');
      setToast('Сообщение скопировано');
    } catch (e) {
      setToast('Не удалось скопировать');
    }
    setMenuMsg(null);
  };

  // Начать редактирование своего сообщения
  const startEdit = (msg) => {
    setMenuMsg(null);
    setEditingMsg(msg);
    setEditText(msg.text || '');
  };

  const cancelEdit = () => {
    setEditingMsg(null);
    setEditText('');
  };

  // Сохранить изменения (серверное действие edit подключится после его добавления)
  const saveEdit = async () => {
    const text = (editText || '').trim();
    if (!text || !editingMsg) return;
    const a = getAuth();
    if (!a || !activeStream) return;
    if (text === editingMsg.text) { cancelEdit(); return; }
    setChatError(null);
    try {
      const d = await chatEditMessage(a.login, a.token, activeStream.id, editingMsg.id, text);
      if (d.success && d.message) {
        // сервер вернёт обновлённое сообщение: заменяем в ленте
        setMessages(prev => prev.map(m => (m.id === editingMsg.id ? d.message : m)));
        setToast('Сообщение обновлено');
        cancelEdit();
      } else {
        // Сервер (пока) не умеет edit — понятное сообщение, код уже готов
        setChatError((d && d.error) || 'Редактирование станет доступно после обновления сервера');
        cancelEdit();
      }
    } catch (e) {
      setChatError('Не удалось связаться с сервером.');
      cancelEdit();
    }
  };


  // Обработчики долгого нажатия (тач) и правого клика (ПК) — открывают меню сообщения
  const holdHandlers = (msg) => {
    const startHold = (e) => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
      holdTimerRef.current = setTimeout(() => openMenu(msg), 520);
    };
    const cancelHold = () => {
      if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
    };
    return {
      onTouchStart: startHold,
      onTouchMove: cancelHold,
      onTouchEnd: cancelHold,
      onContextMenu: (e) => { e.preventDefault(); openMenu(msg); }
    };
  };

  // --- отправка ---------------------------------------------------------------
  const send = async () => {
    const text = (draft || '').trim();
    if (!text || sending || !activeStream) return;
    const a = getAuth();
    if (!a) return;
    setSending(true);
    setChatError(null);
    try {
      const d = await chatSendMessage(a.login, a.token, activeStream.id, text);
      if (d.success) {
        setDraft('');
        if (d.message) setMessages(prev => [...prev, d.message]);
        // Своё сообщение прочитано: сразу отмечаем, чтобы индикатор на FAB
        // не оставался «непрочитанным» до повторного открытия комнаты.
        try { await chatMarkRead(a.login, a.token, activeStream.id); } catch (e2) { /* тихо */ }
        setStreams(prev => prev ? prev.map(s => s.id === activeStream.id ? { ...s, unread: false } : s) : prev);
      } else {
        setChatError(d.error || 'Не удалось отправить');
      }
    } catch (e) {
      setChatError('Не удалось связаться с сервером.');
    } finally {
      setSending(false);
    }
  };

  // --- удаление (из меню/кнопки) ---
  const removeMessage = async (msg) => {
    setMenuMsg(null);
    setEditingMsg(null);
    if (!window.confirm('Удалить это сообщение?')) return;
    const a = getAuth();
    if (!a || !activeStream) return;
    try {
      const d = await chatDeleteMessage(a.login, a.token, activeStream.id, msg.id);
      if (d.success && d.deleted) {
        setMessages(prev => prev.filter(m => m.id !== msg.id));
      } else if (d.success && !d.deleted) {
        // сообщения уже нет на сервере — просто обновилось поллингом
        setMessages(prev => prev.filter(m => m.id !== msg.id));
      } else {
        setChatError(d.error || 'Не удалось удалить сообщение');
      }
    } catch (e) {
      setChatError('Не удалось связаться с сервером.');
    }
  };

  // Может ли текущий пользователь удалить это сообщение (визуально)
  const canDeleteMsg = (msg) => {
    if (!myLogin || !msg) return false;
    if (msg.user === myLogin) return true;
    if (role === 'admin' || role === 'mentor') return true;
    return false;
  };

  // --- отчёт по нарушениям (кнопка в шапке комнаты) -------------------------
  // Видна только наставнику и админу: обычный участник не должен рассылать отчёт потоку.
  const canSendReport = role === 'admin' || role === 'mentor';

  const sendReportNow = async () => {
    const a = getAuth();
    if (!a || !activeStream) return;
    const wasBusy = reportBusy;
    if (wasBusy) return;
    setReportBusy(true);
    setChatError(null);
    try {
      // force=true: если сегодня уже отправляли, спросим — отправлять ли ещё раз.
      const d = await reportRunNow(a.login, a.token, activeStream.id, true);
      const r = d && d.results && d.results[0];
      if (d && d.success && r && r.sent) {
        // сразу подтягиваем свежую ленту, чтобы отчёт появился без ожидания поллинга
        const g = await chatGetMessages(a.login, a.token, activeStream.id);
        if (g.success && Array.isArray(g.messages)) setMessages(g.messages);
      } else if (d && d.success && r && r.reason === 'no_members') {
        setChatError('В потоке нет участников — отчёт не отправлен');
      } else if (d && d.success && r && r.reason === 'already_sent_today') {
        setChatError('Отчёт за сегодня уже отправлен');
      } else {
        setChatError((d && d.error) || 'Не удалось отправить отчёт');
      }
    } catch (e) {
      setChatError('Ошибка отправки отчёта');
    }
    setReportBusy(false);
  };

  // --- живой поллинг (5 сек, окно 08–23 МСК) --------------------------------
  useEffect(() => {
    const timer = setInterval(async () => {
      if (!isMskWindowOpen()) return; // ночью отключён
      const a = getAuth();
      if (!a) return;
      // всегда — статус потоков (свежая прочитанность)
      try {
        const d = await chatGetMyStreams(a.login, a.token);
        if (d.success) {
          setStreams(d.streams || []);
        }
      } catch (e) { /* тихо */ }

      // живые обновления открытой комнаты
      const sid = streamIdRef.current;
      if (sid) {
        try {
          const g = await chatGetMessages(a.login, a.token, sid);
          if (g.success) {
            const fresh = g.messages || [];
            setMessages(prev => (deepEqualMessages(prev, fresh) ? prev : fresh));
          }
        } catch (e) { /* тихо */ }
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [getAuth]);

  // автоскролл вниз при появлении новых сообщений/открытии
  useEffect(() => {
    if (listEndRef.current) {
      try { listEndRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' }); } catch (e) {}
    }
  }, [messages.length, loadingMsgs]);

  // === СТИЛИ =================================================================
  const st = {
    root: {
      height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
      background: colors.page, color: colors.text, fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden', boxSizing: 'border-box', position: 'relative'
    },
    header: {
      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
      background: colors.header, flexShrink: 0
    },
    headerBtn: {
      background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
      padding: '6px 10px', borderRadius: 10, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, flexShrink: 0
    },
    headerTitle: { fontSize: 16, fontWeight: 600, color: '#fff', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    headerSub: { fontSize: 11, color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 12 },
    streamCard: {
      background: colors.card, borderRadius: 14, padding: '12px 14px', marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
      border: `1px solid ${colors.border}`
    },
    streamName: { fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    streamMeta: { fontSize: 11, color: colors.textMuted, marginTop: 2 },
    badgeDot: { width: 10, height: 10, borderRadius: '50%', background: '#e53935', flexShrink: 0 },
    newTag: { fontSize: 11, color: '#e53935', fontWeight: 700, flexShrink: 0 },
    empty: { textAlign: 'center', padding: 44, color: colors.textMuted, fontSize: 14, lineHeight: 1.5 },
    errorBar: { margin: '0 12px 8px', padding: '8px 14px', background: colors.error, color: colors.errorText, borderRadius: 10, fontSize: 13 },
    msgArea: { flex: 1, overflowY: 'auto', padding: '12px 12px 4px', display: 'flex', flexDirection: 'column', gap: 10, WebkitOverflowScrolling: 'touch' },
    row: { display: 'flex', width: '100%' },
    rowMine: { justifyContent: 'flex-end' },
    rowOther: { justifyContent: 'flex-start' },
    bubble: { maxWidth: '100%', borderRadius: 16, padding: '9px 13px', fontSize: 15, lineHeight: 1.4, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'normal' },
    bubbleMine: { background: '#1D9E75', color: '#fff', borderBottomRightRadius: 4 },
    bubbleOther: { background: colors.card, color: colors.text, border: `1px solid ${colors.border}`, borderBottomLeftRadius: 4 },
    author: { fontSize: 11, fontWeight: 700, color: '#1D9E75', marginBottom: 2 },
    bubbleMeta: { fontSize: 10, color: 'rgba(255,255,255,0.85)', textAlign: 'right', marginTop: 4 },
    bubbleMetaOther: { fontSize: 10, color: colors.textMuted, textAlign: 'right', marginTop: 4 },
    del: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.75, alignSelf: 'flex-start', flexShrink: 0 },
    inputRow: {
      display: 'flex', gap: 8, padding: '10px 12px', borderTop: `1px solid ${colors.border}`,
      background: colors.card, flexShrink: 0
    },
    textarea: {
      flex: 1, background: colors.inputBg, color: colors.text, border: `1px solid ${colors.inputBorder}`,
      borderRadius: 18, padding: '10px 14px', fontSize: 15, outline: 'none', resize: 'none',
      maxHeight: 110, lineHeight: 1.4, fontFamily: 'inherit'
    },
    sendBtn: {
      width: 42, height: 42, borderRadius: '50%', background: '#1D9E75', color: '#fff', border: 'none',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
    },
    sendDisabled: { opacity: 0.5, cursor: 'default' },
    menuItem: {
      width: '100%', background: 'transparent', border: 'none', borderRadius: 4,
      padding: '13px 26px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer',
      fontSize: 15, color: colors.text, textAlign: 'left'
    }
  };


  const headerUsernames = (s) => {
    const names = [];
    if (s.members && s.members.length) names.push(...s.members);
    if (s.mentors && s.mentors.length) names.push(...s.mentors.map(m => m + ' (наставник)'));
    return names;
  };

  // ===== ЭКРАН: список потоков ==============================================
  if (!activeStream) {
    const showBadge = role === 'admin' || role === 'mentor';
    return (
      <div style={st.root}>
        <div style={st.header}>
          <button style={st.headerBtn} onClick={onClose} title="Закрыть"><Icons.close size={15} color="#fff" /></button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={st.headerTitle}>Чат</div>
            <div style={st.headerSub}>общие комнаты ваших групп</div>
          </div>
          <button style={st.headerBtn} onClick={() => loadStreams(false)} title="Обновить">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>

        {streamsError && <div style={st.errorBar}>{streamsError}</div>}

        {streams === null && <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Загрузка…</div>}

        {streams !== null && streams.length === 0 && (
          <div style={st.scroll}>
            <div style={st.empty}>
              <div style={{ opacity: 0.4, marginBottom: 10 }}><Icons.chat size={44} color={colors.textMuted} /></div>
              Потоков пока нет.<br />
              Когда админ добавит вас в поток или группу — она появится здесь.
            </div>
          </div>
        )}

        {streams !== null && streams.length > 0 && (
          <div style={st.scroll}>
            {streams.map(s => (
              <div key={s.id} style={st.streamCard} onClick={() => openStream(s)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={st.streamName}>{s.name}</div>
                    <div style={st.streamMeta}>{headerUsernames(s).join(', ') || 'группа'}</div>
                  </div>
                </div>
                <Icons.chat size={20} color="#1D9E75" style={{ flexShrink: 0 }} />
                {s.unread
                  ? (showBadge ? <span style={st.newTag}>новые</span> : <span style={st.badgeDot} />)
                  : null}
                <Icons.chevronRight size={18} color={colors.textMuted} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== ЭКРАН: открытая комната ============================================
  return (
    <div style={st.root}>
      <div style={st.header}>
        <button style={st.headerBtn} onClick={closeStream} title="К списку потоков"><Icons.back size={15} color="#fff" /></button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={st.headerTitle}>{activeStream.name}</div>
          <div style={st.headerSub}>общая комната</div>
        </div>
        {/* Кнопка отчёта — только наставнику и админу, рядом с закрытием.
            Отчёт всегда приходит сюда, в чат, поэтому и запуск — отсюда. */}
        {canSendReport && (
          <button style={{ ...st.headerBtn, opacity: reportBusy ? 0.55 : 1, cursor: reportBusy ? 'default' : 'pointer' }}
            onClick={sendReportNow} disabled={reportBusy}
            title={reportBusy ? 'Отправка отчёта…' : 'Отправить отчёт по нарушениям за сегодня в этот чат'}>
            <Icons.stats size={15} color="#fff" />
          </button>
        )}
        <button style={st.headerBtn} onClick={onClose} title="Закрыть чат"><Icons.close size={15} color="#fff" /></button>
      </div>

      {chatError && <div style={st.errorBar}>{chatError}<span style={{ cursor: 'pointer', float: 'right', paddingLeft: 10, display: 'inline-flex' }} onClick={() => setChatError(null)}><Icons.close size={14} color={colors.errorText} /></span></div>}

      <div style={st.msgArea}>
        {loadingMsgs && <div style={{ textAlign: 'center', color: colors.textMuted, padding: 20 }}>Загрузка…</div>}

        {!loadingMsgs && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40, fontSize: 14 }}>
            Пока нет сообщений.<br />Напишите первым!
          </div>
        )}

        {messages.map((m, idx) => {
          const mine = isMine(myLogin, m);
          const prevIsSameAuthor = idx > 0 && messages[idx - 1] && messages[idx - 1].user === m.user;
          const showAuthor = !mine && !prevIsSameAuthor;
          return (
            <div key={m.id || idx} style={{ ...st.row, ...(mine ? st.rowMine : st.rowOther), alignItems: 'flex-end' }}>
              <div style={{ minWidth: 0, maxWidth: '82%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                {showAuthor && <div style={st.author}>{m.user}</div>}
                <div
                  style={{ ...st.bubble, ...(mine ? st.bubbleMine : st.bubbleOther), cursor: 'context-menu', WebkitUserSelect: 'none', userSelect: 'none' }}
                  {...holdHandlers(m)}
                >{m.text}</div>
                <div style={mine ? st.bubbleMeta : st.bubbleMetaOther}>
                  {formatMskTime(m.time)}{m.edited ? ' · изм.' : ''}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={listEndRef} />
      </div>

      {/* Нижняя панель: обычный ввод или редактирование сообщения */}
      {editingMsg ? (
        <div style={{ ...st.inputRow, flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.textSecondary, fontSize: 12 }}>
            <Icons.edit size={14} color="#1D9E75" />
            <span style={{ flex: 1 }}>Редактируете сообщение</span>
            <button onClick={cancelEdit} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textMuted }} title="Отмена"><Icons.close size={16} color={colors.textMuted} /></button>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <textarea
              style={st.textarea}
              rows={1}
              placeholder="Текст сообщения…"
              value={editText}
              onChange={e => setEditText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); } }}
            />
            <button
              style={{ ...st.sendBtn, ...(sending || !editText.trim() ? st.sendDisabled : {}) }}
              disabled={sending || !editText.trim()}
              onClick={saveEdit}
              title="Сохранить изменения"
            >
              <Icons.check size={18} color="#fff" />
            </button>
          </div>
        </div>
      ) : (
        <div style={st.inputRow}>
          <textarea
            style={st.textarea}
            rows={1}
            placeholder="Сообщение… (Enter — отправить, удерживайте сообщение для действий)"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            style={{ ...st.sendBtn, ...(sending || !draft.trim() ? st.sendDisabled : {}) }}
            disabled={sending || !draft.trim()}
            onClick={send}
            title="Отправить"
          >
            <Icons.send size={18} color="#fff" />
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 90, zIndex: 30,
          background: 'rgba(20,20,25,0.92)', color: '#fff', padding: '8px 16px', borderRadius: 18,
          fontSize: 13, boxShadow: '0 4px 14px rgba(0,0,0,0.25)', maxWidth: '86%', textAlign: 'center'
        }}>{toast}</div>
      )}

      {/* ===== Меню сообщения (долгое нажатие) ===== */}
      {menuMsg && (
        <>
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 20,
            display: 'flex', alignItems: 'flex-end'
          }} onClick={() => setMenuMsg(null)}>
            <div style={{ width: '100%', background: colors.card, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '10px 0 18px', animation: 'sheetUp 0.18s ease-out' }}
              onClick={e => e.stopPropagation()}>
              {/* Маркер */}
              <div style={{ width: 40, height: 4, borderRadius: 2, background: colors.border, margin: '0 auto 10px' }} />
              <div style={{ padding: '2px 22px 10px', fontSize: 12, color: colors.textMuted }}>
                от {menuMsg.user} · {formatMskTime(menuMsg.time)}
              </div>

              {/* Редактировать — только своё */}
              {myLogin && menuMsg.user === myLogin && (
                <button onClick={() => startEdit(menuMsg)} style={st.menuItem}>
                  <Icons.edit size={18} color={colors.text} />
                  <span>Редактировать</span>
                </button>
              )}

              {/* Копировать — всегда */}
              <button onClick={() => copyMessage(menuMsg)} style={st.menuItem}>
                <Icons.copy size={18} color={colors.text} />
                <span>Копировать</span>
              </button>

              {/* Удалить — своё всегда; чужие — только по правам (админ/наставник) */}
              {canDeleteMsg(menuMsg) && (
                <button onClick={() => removeMessage(menuMsg)} style={{ ...st.menuItem, color: '#e53935' }}>
                  <Icons.delete size={18} color="#e53935" />
                  <span style={{ color: '#e53935' }}>Удалить</span>
                </button>
              )}

              {/* Отмена */}
              <button onClick={() => setMenuMsg(null)} style={{ ...st.menuItem, borderTop: `1px solid ${colors.border}`, marginTop: 4, color: colors.textMuted }}>
                <span style={{ textAlign: 'center', width: '100%', fontWeight: 600, color: colors.textMuted }}>Отмена</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Экспортируем и утилиту про «окно» для внешних индикаторов, и компонент.
export const mskInfo = { MSK_OFFSET_MS, POLL_MS };
export default ChatPanel;
